import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import ReactMarkdown from "react-markdown";

import { cn } from "@/lib/utils";

const paragraphVariants = cva("text-wrap-balance text-foreground", {
  variants: {
    websiteStyle: {
      bigBold: "font-sans",
      modernClean: "font-sans",
      traditional: "font-serif",
      posterBold: "font-sans",
    },
    size: {
      sm: "text-sm leading-tight",
      md: "text-base leading-normal",
      lg: "text-lg leading-normal",
    },
    bold: {
      medium: "font-medium",
      bold: "font-bold",
    },
  },
  defaultVariants: {
    websiteStyle: "modernClean",
    size: "md",
    bold: "medium",
  },
});

export interface ParagraphProps
  extends React.HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof paragraphVariants> {
  resourceType?: string;
  children?: React.ReactNode;
}

const Paragraph = React.forwardRef<HTMLParagraphElement, ParagraphProps>(
  ({ className, size, websiteStyle, resourceType, children, ...props }, ref) => {
    const isBlog = resourceType === "blog";

    if (isBlog && typeof children === "string") {
      return (
        <div
          className={cn(
            paragraphVariants({ size, websiteStyle, className }),
            "prose prose-sm max-w-none [&_p:last-child]:mb-0 [&_p]:mb-4"
          )}
          data-feedback-eligible
        >
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
            }}
          >
            {children}
          </ReactMarkdown>
        </div>
      );
    }

    return (
      <p
        data-feedback-eligible
        className={cn(paragraphVariants({ size, websiteStyle, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </p>
    );
  }
);
Paragraph.displayName = "Paragraph";

export { Paragraph, paragraphVariants };
